import { useEffect, useState } from "react";

import { TIME_VALUES } from "@/shared";

import { useScheduleDateTimeValues } from "../../../hooks/useScheduleDateTimeValues";
import { TimeValue } from "../../../types";

interface UseTimeInputProps {
  startDateTime: string | null;
  isDatePickerOpen: boolean;
  handleTimeChange: (value: string) => void;
}

export const useTimeInput = ({
  startDateTime,
  isDatePickerOpen,
  handleTimeChange
}: UseTimeInputProps) => {
  const { hoursValues, minutesValues, getFormattedTimeInput } =
    useScheduleDateTimeValues();

  const [isHoursPickerOpen, setIsHoursPickerOpen] = useState<boolean>(false);
  const [isMinutesPickerOpen, setIsMinutesPickerOpen] =
    useState<boolean>(false);

  const [hoursInputValue, setHoursInputValue] = useState<string>(
    startDateTime?.split(":")[0] ?? "00"
  );

  const [minutesInputValue, setMinutesInputValue] = useState<string>(
    startDateTime?.split(":")[1] ?? "00"
  );

  useEffect(() => {
    const [hoursPart = "00", minutesPart = "00"] =
      startDateTime?.split(":") ?? [];

    setHoursInputValue(hoursPart);
    setMinutesInputValue(minutesPart);
  }, [startDateTime]);

  const timeFilterOptions = (
    options: TimeValue[],
    { inputValue }: { inputValue: string }
  ) => {
    const matchingOptions = options.filter(option =>
      option.label.includes(inputValue)
    );

    const nonMatchingOptions = options.filter(
      option => !matchingOptions.includes(option)
    );

    return [...matchingOptions, ...nonMatchingOptions];
  };

  const normalizeHoursValue = (value: string) => {
    const numericValue = Number(value.replaceAll(/\D/g, ""));

    if (Number.isNaN(numericValue)) {
      return "00";
    }

    return Math.min(numericValue, 23).toString().padStart(2, "0");
  };

  const normalizeMinutesValue = (value: string) => {
    const numericValue = Number(value.replaceAll(/\D/g, ""));

    if (Number.isNaN(numericValue)) {
      return "00";
    }

    const clampedMinutes = Math.min(numericValue, 59);

    return clampedMinutes.toString().padStart(2, "0");
  };

  const updateCombinedTime = (hoursValue: string, minutesValue: string) => {
    const formattedTime = `${hoursValue}:${minutesValue}`;

    const normalizedTime =
      getFormattedTimeInput(formattedTime) ?? TIME_VALUES.Midnight;

    handleTimeChange(normalizedTime);
  };

  const handleHoursBlur = (value: string) => {
    const normalizedHours = normalizeHoursValue(value);

    setHoursInputValue(normalizedHours);

    updateCombinedTime(normalizedHours, minutesInputValue);
  };

  const handleHoursOpen = () => {
    !isDatePickerOpen && setIsHoursPickerOpen(true);
  };

  const handleHoursClose = () => {
    setIsHoursPickerOpen(false);
  };

  const handleHoursInputChange = (event: any, newInputValue: string) => {
    if (!event) return;

    const numericValue = newInputValue.replaceAll(/\D/g, "");

    if (numericValue.length <= 2) {
      setHoursInputValue(numericValue);
    }
  };

  const handleHoursChange = (event: any, selectedOption: any) => {
    if (selectedOption?.label) {
      setHoursInputValue(selectedOption.label);

      updateCombinedTime(selectedOption.label, minutesInputValue);
    }

    if (event?.type === "keydown") {
      handleHoursBlur(event.target.value);
    }
  };

  const handleMinutesBlur = (value: string) => {
    const normalizedMinutes = normalizeMinutesValue(value);

    setMinutesInputValue(normalizedMinutes);

    updateCombinedTime(hoursInputValue, normalizedMinutes);
  };

  const handleMinutesOpen = () => {
    !isDatePickerOpen && setIsMinutesPickerOpen(true);
  };

  const handleMinutesClose = () => {
    setIsMinutesPickerOpen(false);
  };

  const handleMinutesInputChange = (event: any, newInputValue: string) => {
    if (!event) return;

    const numericValue = newInputValue.replaceAll(/\D/g, "");

    if (numericValue.length <= 2) {
      setMinutesInputValue(numericValue);
    }
  };

  const handleMinutesChange = (event: any, selectedOption: any) => {
    if (selectedOption?.label) {
      setMinutesInputValue(selectedOption.label);

      updateCombinedTime(hoursInputValue, selectedOption.label);
    }

    if (event?.type === "keydown") {
      handleMinutesBlur(event.target.value);
    }
  };

  return {
    hoursValues,
    minutesValues,
    isHoursPickerOpen,
    isMinutesPickerOpen,
    hoursInputValue,
    minutesInputValue,
    timeFilterOptions,
    handleHoursBlur,
    handleHoursOpen,
    handleHoursClose,
    handleHoursInputChange,
    handleHoursChange,
    handleMinutesBlur,
    handleMinutesOpen,
    handleMinutesClose,
    handleMinutesInputChange,
    handleMinutesChange
  };
};
